import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';
import { makeRequest2 } from '../../../../utils/makeRequest';
import { toastify } from '../../../../utils/toast';
import { useFacilityStore } from '../../../../app/z-store/store';

const GoodsReceivedNotesTab = () => {
  const navigate = useNavigate();
  const toast = useRef(null);
  const fileUploadRef = useRef(null);
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  // State for data and UI
  const [searchTerm, setSearchTerm] = useState('');
  const [goodsReceivedNotes, setGoodsReceivedNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);

  // Available Purchase Orders
  const [availablePOs, setAvailablePOs] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [selectedPODetails, setSelectedPODetails] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    purchaseOrderId: '',
    poNumber: '',
    department: '',
    supplierName: '',
    quantityOrdered: null,
    quantity: null,
    description: '',
    receivedDate: new Date(),
    receivedBy: '',
    notes: '',
    attachments: []
  });

  useEffect(() => {
    if (selectedFacilityId) {
      fetchGoodsReceivedNotes();
      fetchAvailablePOs();
    }
  }, [selectedFacilityId]);

  const fetchGoodsReceivedNotes = async () => {
    try {
      setLoading(true);
      const response = await makeRequest2(
        `/api/app/goods-received-notes/all/${selectedFacilityId}`,
        'GET'
      );
      if (response.success) {
        setGoodsReceivedNotes(response.data?.data || []);
      } else {
        setGoodsReceivedNotes([]);
      }
    } catch (error) {
      console.error('Error fetching goods received notes:', error);
      setGoodsReceivedNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailablePOs = async () => {
    try {
      const response = await makeRequest2(
        `/api/app/purchase-orders/all/${selectedFacilityId}`,
        'GET'
      );
      if (response.success) {
        const poOptions = response.data?.data
          ?.filter(po => po.status === 'approved' || po.status === 'sent') // Only show approved/sent POs
          ?.map(po => ({
            label: `${po.poNumber} - ${po.supplierInfo?.name || 'Unknown Supplier'}`,
            value: po._id,
            poData: po // Store full PO data
          })) || [];
        setAvailablePOs(poOptions);
      }
    } catch (error) {
      console.error('Error fetching available POs:', error);
      setAvailablePOs([]);
    }
  };

  // Handle PO selection and prefill form data
  const handlePOSelection = (selectedOption) => {
    setSelectedPO(selectedOption);
    
    if (selectedOption && selectedOption.poData) {
      const poData = selectedOption.poData;
      setSelectedPODetails(poData);
      
      // Calculate total quantity from all items
      const totalQuantity = poData.items ? 
        poData.items.reduce((sum, item) => sum + item.quantity, 0) : 0;
      
      // Generate items description
      const itemsDescription = poData.items ? 
        poData.items.map(item => 
          `${item.itemDescription} (${item.quantity} ${item.unitOfMeasure})`
        ).join(', ') : '';

      setFormData(prev => ({
        ...prev,
        purchaseOrderId: poData._id,
        poNumber: poData.poNumber,
        department: poData.department || '',
        supplierName: poData.supplierInfo?.name || '',
        quantityOrdered: totalQuantity,
        description: itemsDescription
      }));
    } else {
      // Clear form when no PO is selected
      setSelectedPODetails(null);
      setFormData(prev => ({
        ...prev,
        purchaseOrderId: '',
        poNumber: '',
        department: '',
        supplierName: '',
        quantityOrdered: null,
        description: ''
      }));
    }
  };

  const filteredNotes = Array.isArray(goodsReceivedNotes)
    ? goodsReceivedNotes.filter((item) => {
      const poNumber = (item.poNumber || '').toLowerCase();
      const description = (item.description || '').toLowerCase();
      const receivedBy = (item.receivedBy || '').toLowerCase();

      return (
        poNumber.includes(searchTerm.toLowerCase()) ||
        description.includes(searchTerm.toLowerCase()) ||
        receivedBy.includes(searchTerm.toLowerCase())
      );
    })
    : [];

  // Date formatting function
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";

      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'long' }).toUpperCase();
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    } catch (error) {
      return "Invalid Date";
    }
  };

  // Action template for datatable
  const actionTemplate = (rowData) => {
    return (
      <div className="d-flex gap-2">
        <Button
          icon="ti ti-eye"
          className="p-button-rounded p-button-info p-button-sm"
          onClick={() => handleViewDetails(rowData)}
          tooltip="View Details"
        />
        <Button
          icon="ti ti-edit"
          className="p-button-rounded p-button-warning p-button-sm"
          onClick={() => handleEditNote(rowData)}
          tooltip="Edit"
        />
        <Button
          icon="ti ti-trash"
          className="p-button-rounded p-button-danger p-button-sm"
          onClick={() => handleDeleteNote(rowData._id)}
          tooltip="Delete"
        />
      </div>
    );
  };

  const handleViewDetails = (noteData) => {
    toast.current.show({
      severity: 'info',
      summary: 'View Details',
      detail: `Viewing details for PO: ${noteData.poNumber}`,
    });
  };

  const handleEditNote = (noteData) => {
    setEditMode(true);
    setSelectedNote(noteData);

    // Find the corresponding PO for editing
    const correspondingPO = availablePOs.find(po => 
      po.poData.poNumber === noteData.poNumber || po.poData._id === noteData.purchaseOrderId
    );
    
    if (correspondingPO) {
      setSelectedPO(correspondingPO);
      setSelectedPODetails(correspondingPO.poData);
    }

    setFormData({
      purchaseOrderId: noteData.purchaseOrderId || '',
      poNumber: noteData.poNumber || '',
      department: noteData.department || '',
      supplierName: noteData.supplierName || '',
      quantityOrdered: noteData.quantityOrdered || null,
      quantity: noteData.quantity || null,
      description: noteData.description || '',
      receivedDate: new Date(noteData.receivedDate || noteData.date) || new Date(),
      receivedBy: noteData.receivedBy || '',
      notes: noteData.notes || '',
      attachments: []
    });
    setShowAddModal(true);
  };

  const handleDeleteNote = (noteId) => {
    confirmDialog({
      message: 'Are you sure you want to delete this goods received note?',
      header: 'Confirmation',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          setLoading(true);
          const response = await makeRequest2(
            `/api/app/goods-received-notes/delete/${noteId}`,
            'DELETE'
          );
          if (response.success) {
            toastify('Goods received note deleted successfully', 'success');
            fetchGoodsReceivedNotes();
          } else {
            toastify(response.error || 'Failed to delete goods received note', 'error');
          }
        } catch (error) {
          toastify('Error deleting goods received note', 'error');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleAddNew = () => {
    setEditMode(false);
    setSelectedNote(null);
    setSelectedPO(null);
    setSelectedPODetails(null);
    setFormData({
      purchaseOrderId: '',
      poNumber: '',
      department: '',
      supplierName: '',
      quantityOrdered: null,
      quantity: null,
      description: '',
      receivedDate: new Date(),
      receivedBy: '',
      notes: '',
      attachments: []
    });
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditMode(false);
    setSelectedNote(null);
    setSelectedPO(null);
    setSelectedPODetails(null);
    setFormData({
      purchaseOrderId: '',
      poNumber: '',
      department: '',
      supplierName: '',
      quantityOrdered: null,
      quantity: null,
      description: '',
      receivedDate: new Date(),
      receivedBy: '',
      notes: '',
      attachments: []
    });
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }
  };

  const handleSubmit = async () => {
    try {
      // Enhanced validation
      if (!formData.purchaseOrderId) {
        toastify('Please select a Purchase Order', 'error');
        return;
      }

      if (!formData.quantity || formData.quantity <= 0) {
        toastify('Please enter a valid quantity received', 'error');
        return;
      }

      if (!formData.description.trim()) {
        toastify('Please enter a description', 'error');
        return;
      }

      if (!formData.receivedBy.trim()) {
        toastify('Please enter who received the goods', 'error');
        return;
      }

      // Validate quantity doesn't exceed ordered quantity
      if (formData.quantity > formData.quantityOrdered) {
        toastify('Quantity received cannot exceed quantity ordered', 'error');
        return;
      }

      setLoading(true);

      const submitData = new FormData();
      submitData.append('purchaseOrderId', formData.purchaseOrderId);
      submitData.append('poNumber', formData.poNumber);
      submitData.append('department', formData.department);
      submitData.append('supplierName', formData.supplierName);
      submitData.append('quantityOrdered', formData.quantityOrdered.toString());
      submitData.append('quantity', formData.quantity.toString());
      submitData.append('description', formData.description);
      submitData.append('receivedDate', formData.receivedDate.toISOString());
      submitData.append('receivedBy', formData.receivedBy);
      submitData.append('notes', formData.notes);
      submitData.append('facilityId', selectedFacilityId);

      // Append files
      formData.attachments.forEach((file, index) => {
        submitData.append('attachments', file);
      });

      const url = editMode
        ? `/api/app/goods-received-notes/update/${selectedNote._id}`
        : `/api/app/goods-received-notes/create`;

      const method = editMode ? 'PUT' : 'POST';

      const response = await makeRequest2(url, method, submitData);

      if (response.success) {
        toastify(
          editMode
            ? 'Goods received note updated successfully'
            : 'Goods received note created successfully',
          'success'
        );
        handleCloseModal();
        fetchGoodsReceivedNotes();
      } else {
        toastify(response.error || 'Failed to save goods received note', 'error');
      }
    } catch (error) {
      console.error('Error saving goods received note:', error);
      toastify('Error saving goods received note', 'error');
    } finally {
      setLoading(false);
    }
  };

  const onFileSelect = (e) => {
    setFormData(prev => ({
      ...prev,
      attachments: [...e.files]
    }));
  };

  const onFileRemove = (file, callback) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter(f => f !== file)
    }));
    callback();
  };

  if (!selectedFacilityId) {
    return (
      <div className="card">
        <div className="card-body text-center">
          <div className="alert alert-warning" role="alert">
            <i className="ti ti-alert-triangle me-2"></i>
            Please select a facility to view goods received notes.
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="card">
        <div className="card-body">
          {/* Search and Add Button */}
          <div className="row mb-3">
            <div className="col-md-4">
              <div className="p-inputgroup">
                <span className="p-inputgroup-addon">
                  <i className="ti ti-search"></i>
                </span>
                <InputText
                  placeholder="Search by PO number, description, received by..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-control"
                />
              </div>
            </div>
            <div className="col-md-8 text-end">
              <Button
                label="Add GRN"
                className="btn btn-primary"
                onClick={handleAddNew}
              />
            </div>
          </div>

          {/* Data Table */}
          <div className="table-responsive">
            <DataTable
              value={filteredNotes}
              emptyMessage="No goods received notes found."
              sortMode="multiple"
              paginator
              rows={10}
              stripedRows
              loading={loading}
              tableStyle={{ minWidth: '50rem' }}
              stateStorage="session"
              stateKey="goods-received-notes-state"
            >
              <Column
                field="receivedDate"
                header="RECEIVED DATE"
                sortable
                body={(rowData) => formatDate(rowData.receivedDate || rowData.date)}
              />
              <Column field="poNumber" header="PO NUMBER" sortable />
              <Column
                field="quantity"
                header="QUANTITY RECEIVED"
                sortable
                body={(rowData) => rowData.quantity?.toLocaleString() || '0'}
              />
              <Column field="receivedBy" header="RECEIVED BY" sortable />
              <Column field="supplierName" header="SUPPLIER" sortable />
              <Column
                header="ACTIONS"
                body={actionTemplate}
                style={{ width: '12rem' }}
              />
            </DataTable>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog
        header={editMode ? "Edit Goods Received Note" : "Add Goods Received Note"}
        visible={showAddModal}
        onHide={handleCloseModal}
        style={{ width: '70vw' }}
        breakpoints={{ '960px': '85vw', '641px': '95vw' }}
        footer={
          <div className="text-end mt-4">
            <Button
              type="button"
              className="btn btn-outline-secondary me-2"
              onClick={handleCloseModal}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Submit'}
            </Button>
          </div>
        }
      >
        <div className="container">
          <div className="row">
            {/* Purchase Order Selection */}
            <div className="col-md-12 mb-3">
              <label htmlFor="purchaseOrder" className="form-label">
                Select Purchase Order <span style={{ color: "red" }}>*</span>
              </label>
              <Dropdown
                id="purchaseOrder"
                value={selectedPO}
                options={availablePOs}
                onChange={(e) => handlePOSelection(e.value)}
                placeholder="Select a Purchase Order"
                className="w-100"
                disabled={loading || editMode}
                filter
                showClear
                emptyMessage="No approved purchase orders found"
              />
            </div>

            {/* PO Details Row */}
            {selectedPODetails && (
              <div className="col-md-12 mb-3">
                <div className="card bg-light">
                  <div className="card-body p-3">
                    <h6 className="card-title mb-2">Purchase Order Details</h6>
                    <div className="row">
                      <div className="col-md-3">
                        <small className="text-muted">PO Number:</small>
                        <div className="fw-bold">{selectedPODetails.poNumber}</div>
                      </div>
                      <div className="col-md-3">
                        <small className="text-muted">Supplier:</small>
                        <div className="fw-bold">{selectedPODetails.supplierInfo?.name || 'N/A'}</div>
                      </div>
                      <div className="col-md-3">
                        <small className="text-muted">Department:</small>
                        <div className="fw-bold">{selectedPODetails.department || 'N/A'}</div>
                      </div>
                      <div className="col-md-3">
                        <small className="text-muted">Total Quantity:</small>
                        <div className="fw-bold">{formData.quantityOrdered?.toLocaleString() || '0'}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quantity Received */}
            <div className="col-md-6 mb-3">
              <label htmlFor="quantity" className="form-label">
                Quantity Received <span style={{ color: "red" }}>*</span>
              </label>
              <InputNumber
                id="quantity"
                value={formData.quantity}
                onValueChange={(e) => setFormData(prev => ({ ...prev, quantity: e.value }))}
                placeholder="Enter quantity received"
                className="w-100"
                min={1}
                max={formData.quantityOrdered}
                disabled={loading}
              />
              {formData.quantityOrdered && (
                <small className="text-muted">
                  Maximum: {formData.quantityOrdered.toLocaleString()}
                </small>
              )}
            </div>

            {/* Received Date */}
            <div className="col-md-6 mb-3">
              <label htmlFor="receivedDate" className="form-label">
                Received Date <span style={{ color: "red" }}>*</span>
              </label>
              <Calendar
                id="receivedDate"
                value={formData.receivedDate}
                onChange={(e) => setFormData(prev => ({ ...prev, receivedDate: e.value }))}
                dateFormat="dd/mm/yy"
                className="w-100"
                showIcon
                disabled={loading}
                maxDate={new Date()}
              />
            </div>

            {/* Received By */}
            <div className="col-md-6 mb-3">
              <label htmlFor="receivedBy" className="form-label">
                Received By <span style={{ color: "red" }}>*</span>
              </label>
              <InputText
                id="receivedBy"
                value={formData.receivedBy}
                onChange={(e) => setFormData(prev => ({ ...prev, receivedBy: e.target.value }))}
                placeholder="Enter name of person who received"
                className="form-control"
                disabled={loading}
              />
            </div>

            {/* Notes */}
            <div className="col-md-6 mb-3">
              <label htmlFor="notes" className="form-label">
                Additional Notes
              </label>
              <InputTextarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Enter any additional notes"
                rows={3}
                className="form-control"
                disabled={loading}
              />
            </div>

            {/* Description/Items - Auto-filled from PO */}
            <div className="col-md-12 mb-3">
              <label htmlFor="description" className="form-label">
                Items Description <span style={{ color: "red" }}>*</span>
              </label>
              <InputTextarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Items description (auto-filled from PO)"
                rows={3}
                className="form-control"
                disabled={loading}
              />
            </div>

            {/* Attachments */}
            <div className="col-md-12 mb-3">
              <label className="form-label">Attachments</label>
              <FileUpload
                ref={fileUploadRef}
                mode="basic"
                multiple
                accept="image/*,.pdf,.doc,.docx,.xlsx,.xls"
                maxFileSize={5000000} // 5MB
                onSelect={onFileSelect}
                onRemove={onFileRemove}
                chooseLabel="Choose Files"
                className="form-control"
                auto={false}
                disabled={loading}
              />
              <small className="text-muted">
                Accepted formats: Images, PDF, Word, Excel (Max 5MB per file)
              </small>
            </div>

            {/* Selected Files Display */}
            {formData.attachments.length > 0 && (
              <div className="col-md-12">
                <h6>Selected Files:</h6>
                <div className="card p-3">
                  <ul className="list-unstyled mb-0">
                    {formData.attachments.map((file, index) => (
                      <li key={index} className="d-flex align-items-center mb-2">
                        <i className="ti ti-file me-2 text-primary"></i>
                        <span className="me-2">{file.name}</span>
                        <span className="badge bg-light text-dark">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default GoodsReceivedNotesTab;